import { HECTARE_TO_ACRE } from 'common/defines/clients';
import { DATE_STANDARD_FORMAT, DATE_VISUALIZE_FORMAT } from 'constants/date';
import ReactECharts from 'echarts-for-react';
import { IYieldSummary, WeightUnitEnum } from 'interfaces/erms';
import moment from 'moment';
import { FC, useEffect, useMemo, useRef } from 'react';

interface YieldLineChartProps {
  yieldChartData: IYieldSummary[];
  yieldUnit: WeightUnitEnum;
  area: number;
  totalTree: number;
  setHoverDatePoint: (arg: string) => void;
}

const YieldLineChart: FC<YieldLineChartProps> = ({ yieldChartData, yieldUnit, area, totalTree, setHoverDatePoint }) => {
  const chartRef = useRef<echarts.ECharts | null>(null);

  const xAxisData = yieldChartData.map((item) => item._id);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.on('mouseover', (params) => {
        setHoverDatePoint(params.name);
      });
    }
  }, [setHoverDatePoint]);

  const seriesData = useMemo(() => {
    let defaultSeriesData = yieldChartData.map((item) => item.totalWeighbridgeNetWeight);
    switch (yieldUnit) {
      case WeightUnitEnum.KILOGRAM:
        return defaultSeriesData.map((item) => item * 1000);
      case WeightUnitEnum.METRIC_TONNE_PER_HECTARE:
        if (!area) return [];
        return defaultSeriesData.map((item) => item / area);
      case WeightUnitEnum.KILOGRAM_PER_HECTARE:
        if (!area) return [];
        return defaultSeriesData.map((item) => (item * 1000) / area);
      case WeightUnitEnum.METRIC_TONNE_PER_TREE:
        if (!totalTree) return [];
        return defaultSeriesData.map((item) => item / totalTree);
      case WeightUnitEnum.KILOGRAM_PER_TREE:
        if (!totalTree) return [];
        return defaultSeriesData.map((item) => (item * 1000) / totalTree);
      case WeightUnitEnum.METRIC_TONNE_PER_ACRE:
        if (!area) return [];
        return defaultSeriesData.map((item) => item / area / HECTARE_TO_ACRE);
      case WeightUnitEnum.KILOGRAM_PER_ACRE:
        if (!area) return [];
        return defaultSeriesData.map((item) => (item * 1000) / area / HECTARE_TO_ACRE);
      case WeightUnitEnum.KILOGRAM_PER_BUNCH:
        return yieldChartData.map((item) => {
          const { totalHvOverripe, totalHvRipe, totalHvUnderripe } = item;
          const totalFFB = totalHvOverripe + totalHvRipe + totalHvUnderripe;
          return (item.totalWeighbridgeNetWeight * 1000) / totalFFB;
        });

      case WeightUnitEnum.METRIC_TONNE:
      default:
        return defaultSeriesData;
    }
  }, [area, totalTree, yieldChartData, yieldUnit]);

  const option = {
    grid: {
      left: '15%',
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLabel: {
        rotate: 60,
        align: 'center',
        formatter: (value: string) => {
          return moment(value, DATE_STANDARD_FORMAT).format(DATE_VISUALIZE_FORMAT);
        },
        margin: 40,
      },
    },
    yAxis: {
      type: 'value',
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const formattedDate = moment(params[0].name, DATE_STANDARD_FORMAT).format(DATE_VISUALIZE_FORMAT);
        return `${params[0].value.toFixed(3)} (${formattedDate})`;
      },
      backgroundColor: 'rgba(50,50,50,0.7)',
      borderColor: '#333',
      borderWidth: 1,
      textStyle: {
        color: '#fff',
      },
      padding: [5, 10],
      extraCssText: 'box-shadow: 0 0 8px rgba(0, 0, 0, 0.3);',
    },
    series: [
      {
        data: seriesData,
        type: 'line',
        symbol: 'circle',
        symbolSize: 10,
        emphasis: {
          itemStyle: {
            color: '#000000',
            borderWidth: 0,
          },
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      notMerge={true}
      lazyUpdate={true}
      theme={'theme_name'}
      onChartReady={(chartInstance) => {
        chartRef.current = chartInstance;
      }}
    />
  );
};

export default YieldLineChart;
